import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Star, Check, X, Trash2, Search, ChevronLeft, ChevronRight, Filter } from 'lucide-react'

interface Review {
    id: number
    type: 'tour' | 'vehicle'
    item_id: number
    item_title?: string
    user_name?: string
    guest_name?: string
    guest_email?: string
    rating: number
    comment: string
    status: 'pending' | 'approved' | 'rejected'
    created_at: string
}

export default function ReviewsManagement() {
    const [reviews, setReviews] = useState<Review[]>([])
    const [loading, setLoading] = useState(true)
    const [filter, setFilter] = useState<string>('all')
    const [typeFilter, setTypeFilter] = useState<string>('all')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)

    useEffect(() => {
        fetchReviews()
    }, [filter, typeFilter, page])

    const fetchReviews = async () => {
        setLoading(true)
        try {
            const res = await fetch(`/api/admin/reviews?status=${filter}&type=${typeFilter}&page=${page}&search=${search}`)
            const data = await res.json()
            if (data.success) {
                setReviews(data.reviews)
                setTotalPages(data.totalPages || 1)
            }
        } catch (error) {
            console.error('Failed to fetch reviews:', error)
        } finally {
            setLoading(false)
        }
    }

    const updateStatus = async (id: number, type: string, status: string) => {
        try {
            const res = await fetch(`/api/admin/reviews/${type}/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status })
            })
            if ((await res.json()).success) {
                setReviews(prev => prev.map(r => r.id === id && r.type === type ? { ...r, status: status as any } : r))
            }
        } catch (error) {
            console.error('Failed to update:', error)
        }
    }

    const deleteReview = async (id: number, type: string) => {
        if (!confirm('Delete this review?')) return
        try {
            const res = await fetch(`/api/admin/reviews/${type}/${id}`, { method: 'DELETE' })
            if ((await res.json()).success) {
                setReviews(prev => prev.filter(r => !(r.id === id && r.type === type)))
            }
        } catch (error) {
            console.error('Failed to delete:', error)
        }
    }

    const statusColors: Record<string, string> = {
        pending: 'bg-yellow-100 text-yellow-800',
        approved: 'bg-green-100 text-green-800',
        rejected: 'bg-red-100 text-red-800'
    }

    const renderStars = (rating: number) => (
        <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map(star => (
                <Star key={star} className={`w-3.5 h-3.5 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
            ))}
        </div>
    )

    return (
        <>
            <Helmet><title>Reviews Management | Admin</title></Helmet>

            <div className="p-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Star className="w-6 h-6 text-yellow-500" />
                        Reviews Management
                    </h1>
                    <p className="text-gray-600">Manage tour and vehicle reviews</p>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-center justify-between">
                        <div className="flex gap-4">
                            {/* Status Filter */}
                            <div className="flex gap-2">
                                {['all', 'pending', 'approved', 'rejected'].map(f => (
                                    <button
                                        key={f}
                                        onClick={() => { setFilter(f); setPage(1) }}
                                        className={`px-3 py-1.5 rounded-full text-sm capitalize ${filter === f ? 'bg-primary-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                                    >
                                        {f}
                                    </button>
                                ))}
                            </div>

                            {/* Type Filter */}
                            <div className="flex items-center gap-2 border-l pl-4">
                                <Filter className="w-4 h-4 text-gray-400" />
                                <select
                                    value={typeFilter}
                                    onChange={e => { setTypeFilter(e.target.value); setPage(1) }}
                                    className="border rounded-lg px-2 py-1.5 text-sm"
                                >
                                    <option value="all">All Types</option>
                                    <option value="tour">Tours</option>
                                    <option value="vehicle">Vehicles</option>
                                </select>
                            </div>
                        </div>

                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search reviews..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && fetchReviews()}
                                className="pl-10 pr-4 py-2 border rounded-lg text-sm w-64"
                            />
                        </div>
                    </div>
                </div>

                {/* Reviews Table */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Loading...</div>
                    ) : reviews.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">No reviews found</div>
                    ) : (
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Reviewer</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Rating</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Review</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Item</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Date</th>
                                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {reviews.map(review => (
                                    <tr key={`${review.type}-${review.id}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div>
                                                <p className="font-medium text-sm">{review.user_name || review.guest_name}</p>
                                                {review.guest_email && (
                                                    <p className="text-xs text-gray-400">{review.guest_email}</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">{renderStars(review.rating)}</td>
                                        <td className="px-4 py-3">
                                            <p className="text-sm text-gray-700 line-clamp-2 max-w-md">{review.comment}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${review.type === 'tour' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                                                {review.type === 'tour' ? '🎫' : '🚗'} {review.item_title || `#${review.item_id}`}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs ${statusColors[review.status]}`}>
                                                {review.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                            {new Date(review.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {review.status !== 'approved' && (
                                                    <button onClick={() => updateStatus(review.id, review.type, 'approved')} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Approve">
                                                        <Check className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {review.status !== 'rejected' && (
                                                    <button onClick={() => updateStatus(review.id, review.type, 'rejected')} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Reject">
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button onClick={() => deleteReview(review.id, review.type)} className="p-1.5 text-gray-400 hover:bg-gray-50 hover:text-red-600 rounded" title="Delete">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="flex items-center gap-1 px-3 py-1.5 border rounded disabled:opacity-50"
                            >
                                <ChevronLeft className="w-4 h-4" /> Prev
                            </button>
                            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="flex items-center gap-1 px-3 py-1.5 border rounded disabled:opacity-50"
                            >
                                Next <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}
